import type {JobResponse} from '@api/model';

import type {JobStatus} from '@/lib/job-display';

/**
 * The job lifecycle's four canonical statuses, mirrored from the API.
 *
 * The display vocabulary (labels, colour tones, `JobStatus`/`StatusTone`) now
 * lives in the shared `@/lib/job-display` module so the site-detail Jobs table
 * can reuse it without a cross-module import. This file re-exports it and keeps
 * the jobs-specific lifecycle helpers (order, terminal states) alongside.
 *
 * The lifecycle is `scheduled → in_progress → completed` (or `cancelled`),
 * driven by the check-in / check-out / cancel endpoints — keep server-driven
 * transitions out of the view layer.
 */
export {
  JOB_STATUS_LABEL,
  JOB_STATUS_TONE,
  type JobStatus,
  type StatusTone,
} from '@/lib/job-display';

/** Display order for status chips, board columns, and counts. */
export const JOB_STATUS_ORDER: readonly JobStatus[] = [
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
];

export function getJobStatus(job: Pick<JobResponse, 'status'>): JobStatus {
  return job.status;
}

/** Completed and cancelled are terminal — no further lifecycle transitions. */
export const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set<JobStatus>([
  'completed',
  'cancelled',
]);

export function isTerminalStatus(status: JobStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}
